import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  ActionBarPrimitive,
  MessagePrimitive,
  useAui,
  useAuiState,
  useThread,
} from "@assistant-ui/react";
import {
  AlertCircle,
  Check,
  ChevronRight,
  Copy,
  Paperclip,
  RotateCcw,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { MarkdownText } from "../MarkdownText";
import { SourceCitations } from "../SourceCitations";
import { ArtifactReferenceCard } from "./ArtifactReferenceCard";
import {
  reasoningPhaseStore,
  streamingMetadataStore,
} from "../../adapters/chatModelAdapter";
import { chatErrorStore } from "../../adapters/chatErrorStore";
import { localToRemoteIdMap } from "../../adapters/threadListAdapter";
import { getFileAuthConfig } from "../../lib/cuadraChatClient";
import { useStrings } from "./StringsContext";
import { FeedbackModal } from "./FeedbackModal";

// ─── Authenticated Image ─────────────────────────────────────────

/**
 * Renders an image that may require an Authorization header to fetch.
 *
 * - Relative URLs (proxy mode) and blob/data URLs are used directly since
 *   cookie-based auth or local blobs handle authentication automatically.
 * - Absolute URLs pointing to the API are fetched with a Bearer token, and the
 *   response is converted to a revocable blob URL for the `<img>`.
 */
export function AuthenticatedImage({
  url,
  alt,
  className,
  style,
}: {
  url: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const [src, setSrc] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      url.startsWith("blob:") ||
      url.startsWith("data:") ||
      url.startsWith("/")
    ) {
      setSrc(url);
      setLoading(false);
      return;
    }

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    const config = getFileAuthConfig();
    let cancelled = false;

    const headers: Record<string, string> = {};
    if (config?.sessionToken) {
      headers["Authorization"] = `Bearer ${config.sessionToken}`;
    }

    fetch(url, { headers })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        if (!cancelled) {
          const objUrl = URL.createObjectURL(blob);
          blobUrlRef.current = objUrl;
          setSrc(objUrl);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSrc(url);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [url]);

  if (loading) {
    return (
      <div
        className="cuadra-rounded-2xl cuadra-animate-pulse"
        style={{
          backgroundColor: "var(--cuadra-muted)",
          height: "8rem",
          width: "12rem",
          border: "1px solid var(--cuadra-border-subtle)",
        }}
      />
    );
  }

  return <img src={src} alt={alt} className={className} style={style} />;
}

// ─── Reasoning ───────────────────────────────────────────────────

/**
 * Inline Reasoning component for MessagePrimitive.Parts.
 *
 * Phase machine (driven by reasoningPhaseStore + isStreaming guard):
 *   streaming → clamped live view: "Thinking…" header + elapsed counter
 *   done      → auto-collapsed toggle: chevron + "Reasoning (Ns)"
 *   history   → isStreaming=false forces "done" regardless of store state
 */
function Reasoning({ text }: { text: string }) {
  const [isOpen, setIsOpen] = useState(true);
  const streamBodyRef = useRef<HTMLDivElement>(null);
  const doneBodyRef = useRef<HTMLDivElement>(null);
  const startMsRef = useRef(0);
  const [displaySeconds, setDisplaySeconds] = useState(0);
  const [finalSeconds, setFinalSeconds] = useState<number | null>(null);

  const isStreaming = useAuiState(
    ({ message }) => message?.status?.type === "running",
  );

  const reasoningState = useSyncExternalStore(
    (cb) => reasoningPhaseStore.subscribe(cb),
    () => reasoningPhaseStore.getSnapshot(),
  );

  const str = useStrings();

  // Historical messages: isStreaming=false forces "done" so the singleton
  // store state never leaks into already-complete messages.
  const phase = isStreaming ? reasoningState.phase : "done";

  // Record start time the first time text arrives (once per component mount)
  if (text && startMsRef.current === 0) {
    startMsRef.current = Date.now();
  }

  // Tick elapsed counter while reasoning is actively streaming
  useEffect(() => {
    if (phase !== "streaming") return;
    const id = setInterval(() => {
      if (startMsRef.current > 0) {
        setDisplaySeconds(Math.floor((Date.now() - startMsRef.current) / 1000));
      }
    }, 1000);
    return () => clearInterval(id);
  }, [phase]);

  // Capture final elapsed when reasoning completes (once) and auto-collapse to peek
  useEffect(() => {
    if (phase === "done" && startMsRef.current > 0 && finalSeconds === null) {
      setFinalSeconds(Math.round((Date.now() - startMsRef.current) / 1000));
      setIsOpen(false);
    }
  }, [phase, finalSeconds]);

  // Auto-scroll streaming body to bottom so newest text is always visible
  useEffect(() => {
    if (phase === "streaming" && streamBodyRef.current) {
      streamBodyRef.current.scrollTop = streamBodyRef.current.scrollHeight;
    }
  }, [text, phase]);

  // Scroll done/historical body to bottom so newest text is visible
  useEffect(() => {
    if (phase === "done" && doneBodyRef.current) {
      doneBodyRef.current.scrollTop = doneBodyRef.current.scrollHeight;
    }
  }, [text, phase, isOpen]);

  if (!text) return null;

  // ── Streaming ────────────────────────────────────────────────────
  if (phase === "streaming") {
    return (
      <div
        className="cuadra-mb-3 cuadra-rounded-xl cuadra-border cuadra-border-border cuadra-overflow-hidden"
        style={{ backgroundColor: "var(--cuadra-card)" }}
      >
        {/* Header */}
        <div className="cuadra-flex cuadra-items-center cuadra-gap-2 cuadra-px-3 cuadra-py-2 cuadra-border-b cuadra-border-border/60">
          <span
            className="cuadra-w-1.5 cuadra-h-1.5 cuadra-rounded-full cuadra-animate-pulse cuadra-flex-shrink-0"
            style={{ backgroundColor: "var(--cuadra-primary)" }}
          />
          <span className="cuadra-text-xs cuadra-font-medium cuadra-text-muted-foreground cuadra-font-brand">
            {str.thinking}
          </span>
          {displaySeconds > 0 && (
            <span
              className="cuadra-ml-auto cuadra-text-xs cuadra-font-brand cuadra-tabular-nums cuadra-text-muted-foreground"
              style={{ opacity: 0.5 }}
            >
              {displaySeconds}s
            </span>
          )}
        </div>

        {/* Body — scrollable, gradient mask fades old text at top so newest stays visible */}
        <div
          ref={streamBodyRef}
          className="cuadra-overflow-y-auto cuadra-px-3 cuadra-py-2.5 cuadra-scrollbar-thin"
          style={{
            maxHeight: 140,
            maskImage:
              "linear-gradient(to bottom, transparent 0%, black 20%, black 100%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 0%, black 20%, black 100%)",
          }}
        >
          <div className="cuadra-text-xs cuadra-text-muted-foreground cuadra-font-brand cuadra-leading-relaxed">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {text}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    );
  }

  // ── Done / historical ────────────────────────────────────────────
  const elapsedLabel =
    finalSeconds !== null && finalSeconds > 0 ? ` (${finalSeconds}s)` : "";

  // Peek height when collapsed (capped + bottom fade)
  const PEEK_HEIGHT = 96;

  return (
    <div className="cuadra-mb-3">
      {/* Toggle header */}
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        aria-label="Toggle reasoning"
        className="cuadra-flex cuadra-items-center cuadra-gap-1.5 cuadra-py-0.5 cuadra-text-xs cuadra-text-muted-foreground hover:cuadra-text-foreground cuadra-transition-colors cuadra-font-brand"
      >
        <ChevronRight
          className={`cuadra-h-3 cuadra-w-3 cuadra-shrink-0 cuadra-transition-transform cuadra-duration-200 ${
            isOpen ? "cuadra-rotate-90" : ""
          }`}
        />
        <span>
          {str.reasoning}
          {elapsedLabel}
        </span>
      </button>

      {/* Body — scrollable, always aligned to bottom (most recent text visible) */}
      <div className="cuadra-relative cuadra-mt-1">
        <div
          ref={doneBodyRef}
          className="cuadra-overflow-y-auto cuadra-transition-all cuadra-duration-300 cuadra-ease-out cuadra-pl-4 cuadra-border-l cuadra-border-border cuadra-scrollbar-thin"
          style={{ maxHeight: isOpen ? 300 : PEEK_HEIGHT }}
        >
          <div className="cuadra-text-xs cuadra-text-muted-foreground cuadra-font-brand cuadra-leading-relaxed cuadra-py-1">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {text}
            </ReactMarkdown>
          </div>
        </div>

        {/* Top fade — shown when content overflows to hint there's more above */}
        {!isOpen && (
          <div
            className="cuadra-absolute cuadra-top-0 cuadra-left-0 cuadra-right-0 cuadra-h-8 cuadra-pointer-events-none"
            style={{
              background:
                "linear-gradient(to bottom, var(--cuadra-background), transparent)",
            }}
          />
        )}
      </div>
    </div>
  );
}

// ─── Thinking Indicator ──────────────────────────────────────────

export function ThinkingIndicator() {
  const str = useStrings();
  return (
    <div
      role="status"
      aria-live="polite"
      className="cuadra-flex cuadra-items-center cuadra-gap-2.5 cuadra-text-muted-foreground cuadra-text-sm cuadra-font-brand cuadra-py-3"
    >
      <div className="cuadra-flex cuadra-items-center cuadra-gap-1">
        <span
          className="cuadra-inline-block cuadra-w-1.5 cuadra-h-1.5 cuadra-rounded-full cuadra-animate-bounce"
          style={{
            animationDelay: "0ms",
            backgroundColor: "var(--cuadra-primary)",
            opacity: 0.7,
          }}
        />
        <span
          className="cuadra-inline-block cuadra-w-1.5 cuadra-h-1.5 cuadra-rounded-full cuadra-animate-bounce"
          style={{
            animationDelay: "150ms",
            backgroundColor: "var(--cuadra-primary)",
            opacity: 0.5,
          }}
        />
        <span
          className="cuadra-inline-block cuadra-w-1.5 cuadra-h-1.5 cuadra-rounded-full cuadra-animate-bounce"
          style={{
            animationDelay: "300ms",
            backgroundColor: "var(--cuadra-primary)",
            opacity: 0.3,
          }}
        />
      </div>
      <span>{str.thinking}</span>
    </div>
  );
}

// ─── User Message ────────────────────────────────────────────────

export function UserMessage() {
  return (
    <MessagePrimitive.Root className="cuadra-flex cuadra-w-full cuadra-justify-end cuadra-py-2.5">
      <div className="cuadra-flex cuadra-flex-col cuadra-items-end cuadra-gap-2 cuadra-max-w-[80%]">
        <MessagePrimitive.Attachments
          components={{
            Attachment: () => {
              const attachment = useAuiState(({ attachment }) => attachment);
              if (!attachment) return null;

              const isImage = attachment.type === "image";
              const file = attachment.file;
              const imageContent = attachment.content?.find(
                (c): c is { type: "image"; image: string } =>
                  c.type === "image",
              );

              const localSrc = file ? URL.createObjectURL(file) : undefined;

              return (
                <div className="cuadra-mb-1">
                  {isImage && (localSrc || imageContent?.image) ? (
                    localSrc ? (
                      <img
                        src={localSrc}
                        alt={attachment.name || "Attachment"}
                        className="cuadra-rounded-2xl cuadra-max-h-64 cuadra-max-w-full cuadra-object-contain"
                        style={{
                          border: "1px solid var(--cuadra-border-subtle)",
                        }}
                      />
                    ) : (
                      <AuthenticatedImage
                        url={imageContent!.image}
                        alt={attachment.name || "Attachment"}
                        className="cuadra-rounded-2xl cuadra-max-h-64 cuadra-max-w-full cuadra-object-contain"
                        style={{
                          border: "1px solid var(--cuadra-border-subtle)",
                        }}
                      />
                    )
                  ) : (
                    <div
                      className="cuadra-flex cuadra-items-center cuadra-gap-2 cuadra-rounded-xl cuadra-px-3 cuadra-py-2"
                      style={{
                        backgroundColor: "var(--cuadra-muted)",
                        border: "1px solid var(--cuadra-border-subtle)",
                      }}
                    >
                      <Paperclip className="cuadra-h-3.5 cuadra-w-3.5 cuadra-text-muted-foreground" />
                      <span className="cuadra-text-sm cuadra-text-foreground cuadra-font-brand">
                        {attachment.name || "Attachment"}
                      </span>
                    </div>
                  )}
                </div>
              );
            },
          }}
        />
        <div
          className="cuadra-break-words cuadra-text-foreground cuadra-font-brand"
          style={{
            borderRadius: "1.25rem",
            backgroundColor: "var(--cuadra-muted)",
            paddingLeft: "1rem",
            paddingRight: "1rem",
            paddingTop: "0.625rem",
            paddingBottom: "0.625rem",
            wordBreak: "break-word",
            fontSize: "0.9375rem",
            lineHeight: "1.625",
          }}
        >
          <MessagePrimitive.Parts
            components={{
              Text: ({ text }) => <MarkdownText content={text || ""} />,
            }}
          />
        </div>
      </div>
    </MessagePrimitive.Root>
  );
}

// ─── Copy Button ─────────────────────────────────────────────────

function CopyMessageButton() {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <ActionBarPrimitive.Copy
      copiedDuration={2000}
      onClick={() => {
        setCopied(true);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setCopied(false), 2000);
      }}
      className="cuadra-flex cuadra-items-center cuadra-justify-center cuadra-size-7 cuadra-rounded-md cuadra-text-muted-foreground hover:cuadra-text-foreground hover:cuadra-bg-muted/60 cuadra-transition-colors"
      aria-label={copied ? "Copied" : "Copy message"}
    >
      {copied ? (
        <Check className="cuadra-h-3.5 cuadra-w-3.5" />
      ) : (
        <Copy className="cuadra-h-3.5 cuadra-w-3.5" />
      )}
    </ActionBarPrimitive.Copy>
  );
}

// ─── Message Feedback Buttons ────────────────────────────────────

export function MessageFeedbackButtons({
  activeRating,
  onRate,
}: {
  activeRating?: "positive" | "negative" | null;
  onRate?: (rating: "positive" | "negative") => void;
}) {
  const baseBtn =
    "cuadra-flex cuadra-items-center cuadra-justify-center cuadra-size-7 cuadra-rounded-md cuadra-transition-colors cuadra-cursor-pointer";

  return (
    <>
      <button
        type="button"
        onClick={() => onRate?.("positive")}
        className={`${baseBtn} ${
          activeRating === "positive"
            ? "cuadra-text-green-600"
            : "cuadra-text-muted-foreground hover:cuadra-text-foreground hover:cuadra-bg-muted/60"
        }`}
        aria-label="Good response"
      >
        <ThumbsUp className="cuadra-h-3.5 cuadra-w-3.5" />
      </button>

      <button
        type="button"
        onClick={() => onRate?.("negative")}
        className={`${baseBtn} ${
          activeRating === "negative"
            ? "cuadra-text-red-500"
            : "cuadra-text-muted-foreground hover:cuadra-text-foreground hover:cuadra-bg-muted/60"
        }`}
        aria-label="Bad response"
      >
        <ThumbsDown className="cuadra-h-3.5 cuadra-w-3.5" />
      </button>
    </>
  );
}


// ─── Assistant Message ───────────────────────────────────────────

export function AssistantMessage() {
  const isInProgress = useAuiState(
    ({ message }) => message?.status?.type === "running",
  );
  const hasContent = useAuiState(({ message }) => {
    const content = message?.content;
    if (!content || !Array.isArray(content)) return false;
    return content.some((part) => {
      if (part.type === "text" && part.text && part.text.length > 0)
        return true;
      if (part.type === "reasoning" && part.text && part.text.length > 0)
        return true;
      return false;
    });
  });

  const serverMessageId = useAuiState(({ message }) => {
    const custom = message?.metadata?.custom as
      | Record<string, unknown>
      | undefined;
    return (custom?.cuadraServerMessageId as string) ?? null;
  });

  const chatId = useAuiState(({ message }) => {
    const custom = message?.metadata?.custom as
      | Record<string, unknown>
      | undefined;
    return (custom?.cuadraChatId as string) ?? null;
  });

  const hasCapturedRef = useRef(false);
  const [capturedStreamKey, setCapturedStreamKey] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (isInProgress && hasContent && !hasCapturedRef.current) {
      const currentId = streamingMetadataStore.getCurrentMessageId();
      if (currentId) {
        hasCapturedRef.current = true;
        setCapturedStreamKey(currentId);
      }
    }
  }, [isInProgress, hasContent]);

  const sources = useSyncExternalStore(
    (callback) => streamingMetadataStore.subscribe(callback),
    () => {
      if (capturedStreamKey) {
        const msgSources =
          streamingMetadataStore.getForMessage(capturedStreamKey);
        return msgSources.sources?.length ? msgSources.sources : null;
      }
      if (isInProgress && hasContent) {
        const currentId = streamingMetadataStore.getCurrentMessageId();
        if (currentId) {
          const currentSources =
            streamingMetadataStore.getForMessage(currentId);
          return currentSources.sources?.length ? currentSources.sources : null;
        }
      }
      return null;
    },
    () => null,
  );

  const [feedbackModalRating, setFeedbackModalRating] = useState<
    "positive" | "negative" | null
  >(null);
  const [submittedRating, setSubmittedRating] = useState<
    "positive" | "negative" | null
  >(null);

  return (
    <MessagePrimitive.Root className="cuadra-flex cuadra-w-full cuadra-justify-start cuadra-py-2.5">
      <div
        className="cuadra-text-foreground cuadra-max-w-full cuadra-break-words cuadra-font-brand"
        style={{
          fontSize: "0.9375rem",
          lineHeight: "1.75",
        }}
      >
        <MessagePrimitive.Parts
          components={{
            Text: ({ text }) => <AssistantText text={text || ""} />,
            Reasoning: ({ text }) => <Reasoning text={text || ""} />,
          }}
        />

        {isInProgress && !hasContent && <ThinkingIndicator />}

        {sources && sources.length > 0 && (
          <SourceCitations sources={sources} />
        )}

        <ActionBarPrimitive.Root
          hideWhenRunning
          className="cuadra-flex cuadra-items-center cuadra-gap-1 cuadra-pt-1"
        >
          <MessageFeedbackButtons
            activeRating={submittedRating}
            onRate={(rating) => setFeedbackModalRating(rating)}
          />
          <CopyMessageButton />
        </ActionBarPrimitive.Root>

        {chatId && feedbackModalRating && (
          <FeedbackModal
            isOpen
            onClose={() => setFeedbackModalRating(null)}
            onSubmitted={(rating) => setSubmittedRating(rating)}
            rating={feedbackModalRating}
            chatId={chatId}
            messageId={serverMessageId}
            sources={sources}
          />
        )}
      </div>
    </MessagePrimitive.Root>
  );
}

// ─── Shared markdown components for ReactMarkdown ────────────────
// Used by SegmentMarkdown and ReasoningMarkdown where MarkdownTextPrimitive
// cannot be used (it reads text from assistant-ui part context, not from props).

import { cn } from "../../utils/cn";

const markdownComponents: import("react-markdown").Components = {
  table: ({ className, ...props }) => (
    <table
      className={cn(
        "cuadra-my-5 cuadra-w-full cuadra-border-separate cuadra-border-spacing-0 cuadra-overflow-y-auto cuadra-font-brand",
        className,
      )}
      {...props}
    />
  ),
  th: ({ className, ...props }) => (
    <th
      className={cn(
        "cuadra-bg-muted cuadra-px-4 cuadra-py-2 cuadra-text-left cuadra-font-normal first:cuadra-rounded-tl-lg last:cuadra-rounded-tr-lg [&[align=center]]:cuadra-text-center [&[align=right]]:cuadra-text-right cuadra-font-brand",
        className,
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }) => (
    <td
      className={cn(
        "cuadra-border-b cuadra-border-l cuadra-px-4 cuadra-py-2 cuadra-text-left last:cuadra-border-r [&[align=center]]:cuadra-text-center [&[align=right]]:cuadra-text-right cuadra-font-brand",
        className,
      )}
      {...props}
    />
  ),
  tr: ({ className, ...props }) => (
    <tr
      className={cn(
        "cuadra-m-0 cuadra-border-b cuadra-p-0 first:cuadra-border-t [&:last-child>td:first-child]:cuadra-rounded-bl-lg [&:last-child>td:last-child]:cuadra-rounded-br-lg",
        className,
      )}
      {...props}
    />
  ),
  p: ({ className, ...props }) => (
    <p
      className={cn(
        "cuadra-mb-3 cuadra-mt-3 cuadra-leading-relaxed first:cuadra-mt-0 last:cuadra-mb-0 cuadra-font-brand",
        className,
      )}
      {...props}
    />
  ),
  ul: ({ className, ...props }) => (
    <ul
      className={cn(
        "cuadra-my-3 cuadra-ml-6 cuadra-list-disc [&>li]:cuadra-mt-1 cuadra-font-brand",
        className,
      )}
      {...props}
    />
  ),
  ol: ({ className, ...props }) => (
    <ol
      className={cn(
        "cuadra-my-3 cuadra-ml-6 cuadra-list-decimal [&>li]:cuadra-mt-1 cuadra-font-brand",
        className,
      )}
      {...props}
    />
  ),
  a: ({ className, ...props }) => (
    <a
      className={cn(
        "cuadra-text-primary cuadra-font-medium cuadra-underline cuadra-underline-offset-4 cuadra-font-brand",
        className,
      )}
      {...props}
    />
  ),
};

// ─── Assistant Text with Artifact References ────────────────────

/**
 * Renders a markdown sub-segment using react-markdown directly.
 * We CANNOT use MarkdownText (which wraps MarkdownTextPrimitive) here because
 * MarkdownTextPrimitive reads the text from the assistant-ui part context — not
 * from the children prop — so it would render the FULL part text (including the
 * raw artifact marker) instead of just the sub-segment we want.
 */
const SegmentMarkdown: React.FC<{ content: string }> = ({ content }) => (
  <div className="cuadra-aui-md">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {content}
    </ReactMarkdown>
  </div>
);

const ARTIFACT_MARKER_RE = /\[artifact:([^:]*):([^:]*):([^\]]*)\]/g;

/**
 * Remove both complete `[artifact:id:title:type]` and partial (streaming)
 * `[artifact:...` markers that haven't received a closing bracket yet.
 */
const stripArtifactMarkers = (s: string) =>
  s
    .replace(/\[artifact:[^\]]*\]?/g, "") // complete + incomplete markers
    .replace(/\n{3,}/g, "\n\n") // collapse excess blank lines
    .trim();

function AssistantText({ text }: { text: string }) {
  const parts = useMemo(() => {
    if (!text.includes("[artifact:")) {
      return null;
    }

    const result: React.ReactNode[] = [];
    let lastIndex = 0;
    let foundAny = false;
    let match: RegExpExecArray | null;

    const regex = new RegExp(ARTIFACT_MARKER_RE.source, "g");
    while ((match = regex.exec(text)) !== null) {
      foundAny = true;
      if (match.index > lastIndex) {
        const before = stripArtifactMarkers(text.slice(lastIndex, match.index));
        if (before) {
          result.push(
            <SegmentMarkdown key={`t-${lastIndex}`} content={before} />,
          );
        }
      }
      result.push(
        <ArtifactReferenceCard
          key={`a-${match.index}`}
          artifactId={match[1]}
          title={match[2]}
          type={match[3]}
        />,
      );
      lastIndex = match.index + match[0].length;
    }

    if (!foundAny) {
      // Marker is still streaming (incomplete) — strip it and render remaining text
      const cleaned = stripArtifactMarkers(text);
      return cleaned
        ? [<SegmentMarkdown key="t-partial" content={cleaned} />]
        : [];
    }

    if (lastIndex < text.length) {
      const after = stripArtifactMarkers(text.slice(lastIndex));
      if (after) {
        result.push(<SegmentMarkdown key={`t-${lastIndex}`} content={after} />);
      }
    }

    return result;
  }, [text]);

  if (parts !== null) return <>{parts}</>;
  return <MarkdownText content={text} />;
}

// ─── Chat Error Indicator ────────────────────────────────────────

export function ChatErrorIndicator() {
  const chatError = useSyncExternalStore(
    chatErrorStore.subscribe,
    chatErrorStore.getSnapshot,
  );
  const isRunning = useThread((state) => {
    const s = state as unknown as { isRunning?: boolean };
    return !!s.isRunning;
  });
  const runtimeThreadId = useThread((state) => {
    const s = state as unknown as { threadId?: string };
    return s.threadId ?? "";
  });
  const resolvedChatId =
    localToRemoteIdMap.get(runtimeThreadId) || runtimeThreadId;
  const aui = useAui();
  const str = useStrings();

  const lastUserText = useThread((state) => {
    const msgs = (
      state as unknown as {
        messages?: readonly {
          role?: string;
          content?: Array<{ type?: string; text?: string }> | string;
        }[];
      }
    ).messages;
    if (!msgs) return null;
    for (let i = msgs.length - 1; i >= 0; i--) {
      const m = msgs[i];
      if (m?.role === "user") {
        if (typeof m.content === "string") return m.content;
        if (Array.isArray(m.content)) {
          const textPart = m.content.find((c) => c.type === "text");
          return textPart?.text ?? null;
        }
      }
    }
    return null;
  });

  if (!chatError || isRunning) return null;

  if (chatError.chatId) {
    const matches =
      chatError.chatId === resolvedChatId ||
      chatError.chatId === runtimeThreadId ||
      localToRemoteIdMap.get(runtimeThreadId) === chatError.chatId;
    if (!matches) return null;
  } else {
    return null;
  }

  const handleRetry = () => {
    if (!lastUserText) return;
    chatErrorStore.clear();
    aui.composer().setText(lastUserText);
    setTimeout(() => aui.composer().send(), 10);
  };

  let errorMessage = str.streamErrorRetry;
  if (chatError.isEmptyStream) {
    const failures = chatError.consecutiveEmptyFailures ?? 1;
    errorMessage =
      failures >= 3 ? str.streamEmptyErrorContactSupport : str.streamEmptyError;
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="cuadra-flex cuadra-items-center cuadra-gap-2 cuadra-py-2"
    >
      <button
        type="button"
        onClick={handleRetry}
        className="cuadra-flex cuadra-items-center cuadra-gap-1.5 cuadra-text-sm cuadra-text-muted-foreground hover:cuadra-text-foreground cuadra-transition-colors cuadra-font-brand cuadra-group"
      >
        <AlertCircle
          className="cuadra-h-3.5 cuadra-w-3.5"
          style={{ color: "var(--cuadra-destructive, rgb(239, 68, 68))" }}
        />
        <span>{errorMessage}</span>
        <RotateCcw className="cuadra-h-3 cuadra-w-3 cuadra-opacity-0 group-hover:cuadra-opacity-100 cuadra-transition-opacity" />
      </button>
    </div>
  );
}
